/**
 * MemorySettingsContent — Profile Settings "Memory" tab.
 *
 * Everything the assistant remembers that this person can see: their own
 * preferences, people, work context, temporary facts and notes (only they
 * see these), the project's shared tool facts (IDs it looked up, calls that
 * failed before) and how the connected mailbox writes. Each can be edited or
 * deleted. Nothing here needs setting up.
 */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiCheck, FiCpu, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';

import { DataState } from '../../context/DataProvider';
import * as memoryApi from '../../services/memoryApi';

// Groups in display order. `kinds` decide which memories land in each.
const GROUPS = [
  { title: 'Your preferences', kinds: ['preference'] },
  { title: 'For now', kinds: ['temporary'] },
  { title: 'People', kinds: ['person'] },
  { title: 'Work context', kinds: ['context'] },
  { title: 'Notes', kinds: ['note'] },
  { title: 'How you write mail', kinds: ['mail_style', 'mail_contact'] },
];
const TOOL_KINDS = new Set(['tool_fact', 'tool_default', 'failed_pattern']);

const APP_LABELS = {
  general: 'General',
  jira: 'Jira',
  confluence: 'Confluence',
  microsoft_outlook: 'Outlook',
  microsoft_teams: 'Teams',
  microsoft_todo: 'Microsoft To Do',
  gmail: 'Gmail',
};

const KIND_LABELS = {
  tool_fact: 'Looked up',
  tool_default: 'Default',
  failed_pattern: 'Failed before',
  note: 'Note',
  preference: 'Preference',
  person: 'Person',
  context: 'Context',
  temporary: 'For now',
  mail_style: 'Your style',
  mail_contact: 'Contact',
};

const SOURCE_LABELS = {
  explicit: 'you asked',
  learned: 'from a tool call you approved',
  sent_items: 'from your sent mail',
  approval_edit: 'from your edits to drafts',
};

const appLabel = (app) =>
  APP_LABELS[app] ||
  app.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const errorMessage = (err, fallback) =>
  (typeof err === 'string' ? err : err?.message) || fallback;

const dateOnly = (iso) => (iso ? iso.slice(0, 10) : '');

// "sent:external:greeting" -> "External"; "c@acme.com|name_to_use" -> "c@acme.com"
const describeKey = (memory) => {
  if (memory.kind === 'mail_contact') return memory.key.split('|')[0];
  if (memory.kind === 'mail_style') {
    const audience = memory.audience || memory.key.split(':').slice(-2)[0];
    return audience ? audience[0].toUpperCase() + audience.slice(1) : '';
  }
  if (['preference', 'context'].includes(memory.kind)) return '';
  return memory.key;
};

function MemoryRow({ memory, onDelete, onSave, busy }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(memory.value);
  const [expiresOn, setExpiresOn] = useState(dateOnly(memory.expires_at));

  const status = !memory.is_valid
    ? 'No longer used: a tool rejected it'
    : !memory.is_active
      ? 'Still learning: used once it repeats'
      : null;
  const label = describeKey(memory);

  const save = async () => {
    const ok = await onSave(memory, {
      value: value.trim() !== memory.value ? value.trim() : undefined,
      expiresOn:
        memory.kind === 'temporary' && expiresOn !== dateOnly(memory.expires_at)
          ? expiresOn
          : undefined,
    });
    if (ok) setEditing(false);
  };

  const cancel = () => {
    setEditing(false);
    setValue(memory.value);
    setExpiresOn(dateOnly(memory.expires_at));
  };

  return (
    <li
      className={`flex items-start gap-3 px-3 py-2 ${status && !editing ? 'opacity-60' : ''}`}
    >
      <span className="mt-0.5 w-20 flex-shrink-0 text-[10px] uppercase tracking-wide text-zinc-500">
        {KIND_LABELS[memory.kind] || memory.kind}
      </span>
      <div className="min-w-0 flex-1">
        {label && label !== memory.value && (
          <p className="truncate text-[11px] text-zinc-400">{label}</p>
        )}
        {editing ? (
          <div className="space-y-1.5">
            <textarea
              rows={2}
              maxLength={2000}
              aria-label="Remembered fact"
              className="w-full resize-y rounded-md border border-zinc-700/50 bg-zinc-900/40 p-1.5 text-[12px] text-zinc-200 outline-none focus:border-[#5652CF]"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            {memory.kind === 'temporary' && (
              <label className="flex items-center gap-2 text-[11px] text-zinc-400">
                Until
                <input
                  type="date"
                  className="rounded border border-zinc-700/50 bg-zinc-900/40 px-1.5 py-0.5 text-[11px] text-zinc-200"
                  value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                />
              </label>
            )}
          </div>
        ) : (
          <p className="break-words text-[12px] text-zinc-200">
            {memory.value}
            {memory.kind === 'temporary' && memory.expires_at && (
              <span className="text-zinc-500">
                {' '}
                · until {dateOnly(memory.expires_at)}
              </span>
            )}
          </p>
        )}
        <p className="text-[10px] text-zinc-600">
          {memory.is_personal ? 'Only you' : 'Shared with your project'} ·{' '}
          {SOURCE_LABELS[memory.source] || memory.source}
          {status && ` · ${status}`}
        </p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-0.5">
        {editing ? (
          <>
            <button
              onClick={save}
              disabled={busy || !value.trim()}
              title="Save"
              className="rounded p-1.5 text-emerald-400 hover:bg-emerald-900/30 disabled:opacity-40"
            >
              <FiCheck className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={cancel}
              title="Cancel"
              className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800"
            >
              <FiX className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setEditing(true)}
            disabled={busy}
            title="Edit"
            className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40"
          >
            <FiEdit2 className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={() => onDelete(memory)}
          disabled={busy}
          title="Forget this"
          className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-red-900/30 hover:text-red-400 disabled:opacity-40"
        >
          <FiTrash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

export default function MemorySettingsContent() {
  const { userGoogleId: accessToken } = DataState();
  const [memories, setMemories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    memoryApi
      .listMemories(accessToken)
      .then((res) => !cancelled && setMemories(res?.data?.memories || []))
      .catch((err) => toast.error(errorMessage(err, 'Could not load memory.')))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const sections = useMemo(() => {
    const result = GROUPS.map((group) => ({
      title: group.title,
      items: memories.filter((m) => group.kinds.includes(m.kind)),
    }));
    const byApp = {};
    memories
      .filter((m) => TOOL_KINDS.has(m.kind))
      .forEach((m) => {
        (byApp[m.app] = byApp[m.app] || []).push(m);
      });
    Object.keys(byApp)
      .sort((a, b) => appLabel(a).localeCompare(appLabel(b)))
      .forEach((app) =>
        result.push({ title: appLabel(app), items: byApp[app] }),
      );
    return result.filter((section) => section.items.length > 0);
  }, [memories]);

  const handleDelete = async (memory) => {
    setBusyId(memory.id);
    try {
      await memoryApi.deleteMemory(accessToken, memory.id);
      setMemories((prev) => prev.filter((m) => m.id !== memory.id));
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete it.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSave = async (memory, changes) => {
    if (changes.value === undefined && changes.expiresOn === undefined) {
      return true;
    }
    setBusyId(memory.id);
    try {
      const res = await memoryApi.updateMemory(accessToken, memory.id, changes);
      setMemories((prev) =>
        prev.map((m) => (m.id === memory.id ? res?.data || m : m)),
      );
      return true;
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save it.'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-1">
      <div className="mb-4">
        <h3 className="flex items-center gap-2 text-base font-bold text-zinc-100">
          <FiCpu className="h-4 w-4 text-[#5652CF]" />
          Memory
        </h3>
        <p className="text-[10px] text-zinc-500">
          What the assistant remembers across chats. It learns from what you
          tell it and actions you approve, never from the content of emails or
          documents. Your preferences and notes are only yours; tool facts are
          shared with your project.
        </p>
      </div>

      {isLoading ? (
        <p className="text-[11px] text-zinc-500">Loading…</p>
      ) : sections.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-800 px-3 py-4 text-center text-[11px] text-zinc-500">
          Nothing remembered yet. Tell the assistant about yourself, the people
          you work with, or ask it to &ldquo;remember&rdquo; something.
        </p>
      ) : (
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.title} className="space-y-1.5">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                {section.title}
              </h4>
              <ul className="divide-y divide-zinc-800 rounded-md border border-zinc-800 bg-zinc-900/60">
                {section.items.map((m) => (
                  <MemoryRow
                    key={m.id}
                    memory={m}
                    onDelete={handleDelete}
                    onSave={handleSave}
                    busy={busyId === m.id}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
